// src/pages/LandingPage.js - Photo-led landing page for Mandap
import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';

// Inline styles as CSS-in-JS
const styles = `
:root {
  --ink: #14110d;
  --ink-2: #2a2520;
  --ink-3: #6a6256;
  --paper: #f6f1e6;
  --cream: #ece3d3;
  --teal: #154e4a;
  --teal-deep: #0b3431;
  --rust: #b85a3d;
  --gold: #b58a37;
  --display: "Fraunces", "Times New Roman", serif;
  --sans: "Inter", -apple-system, system-ui, sans-serif;
  --ease: cubic-bezier(.22,.61,.36,1);
  --ease-out: cubic-bezier(.16,1,.3,1);
}

/* Override the .app container when landing page is active */
.app:has(.landing-page) { display: block; height: auto; }

.landing-page * { box-sizing: border-box; margin: 0; padding: 0; }
.landing-page {
  background: var(--paper);
  color: var(--ink);
  font-family: var(--sans);
  -webkit-font-smoothing: antialiased;
  overflow-x: hidden;
  min-height: 100vh;
}
.landing-page a { color: inherit; text-decoration: none; }
.landing-page img { display: block; max-width: 100%; }

/* Type animation primitives */
.landing-page .line { display: block; overflow: hidden; padding-bottom: 0.1em; margin-bottom: -0.1em; }
.landing-page .line > span {
  display: inline-block; transform: translateY(110%);
  transition: transform 1.1s var(--ease-out);
}
.landing-page .line.in > span { transform: translateY(0); }
.landing-page .line.d1 > span { transition-delay: .08s; }
.landing-page .line.d2 > span { transition-delay: .18s; }
.landing-page .line.d3 > span { transition-delay: .28s; }
.landing-page .line.d4 > span { transition-delay: .38s; }

.landing-page .fade { opacity: 0; transform: translateY(20px); transition: opacity 1s var(--ease-out), transform 1s var(--ease-out); }
.landing-page .fade.in { opacity: 1; transform: none; }
.landing-page .fade.d1 { transition-delay: .15s; }
.landing-page .fade.d2 { transition-delay: .3s; }
.landing-page .fade.d3 { transition-delay: .45s; }
.landing-page .fade.d4 { transition-delay: .6s; }
.landing-page .fade.d5 { transition-delay: .75s; }

.landing-page .reveal { clip-path: inset(100% 0 0 0); transition: clip-path 1.4s var(--ease-out); }
.landing-page .reveal.in { clip-path: inset(0 0 0 0); }
.landing-page .reveal.d1 { transition-delay: .1s; }
.landing-page .reveal.d2 { transition-delay: .25s; }
.landing-page .reveal.d3 { transition-delay: .4s; }

.landing-page .kenburns { transition: transform 14s linear; transform: scale(1.06); }
.landing-page .kenburns.in { transform: scale(1); }

.landing-page .eyebrow { font-size: 12px; letter-spacing: .22em; text-transform: uppercase; font-weight: 500; color: var(--ink-3); }
.landing-page .eyebrow.lt { color: rgba(246,241,230,.55); }

/* NAV */
.landing-page nav.top {
  position: fixed; inset: 0 0 auto 0;
  z-index: 50;
  display: grid; grid-template-columns: 1fr auto 1fr; align-items: center;
  padding: 18px 36px;
  color: white;
  font-family: var(--sans);
  transition: background-color .35s var(--ease), backdrop-filter .35s var(--ease), color .35s var(--ease), padding .35s var(--ease), border-color .35s var(--ease);
  border-bottom: 1px solid transparent;
}
.landing-page nav.top.scrolled {
  background: rgba(246,241,230,.86);
  -webkit-backdrop-filter: saturate(140%) blur(14px);
  backdrop-filter: saturate(140%) blur(14px);
  color: var(--ink);
  padding: 12px 36px;
  border-bottom-color: rgba(20,17,13,.08);
}
.landing-page nav.top.scrolled .cta { border-color: rgba(20,17,13,.4); }
.landing-page nav.top.scrolled .cta:hover { background: var(--ink); color: white; border-color: var(--ink); }
.landing-page nav.top .brand { font-family: var(--display); font-weight: 400; font-size: 22px; letter-spacing: -.01em; }
.landing-page nav.top .brand em { font-style: italic; }
.landing-page nav.top .links { justify-self: center; display: flex; gap: 36px; font-size: 13px; font-weight: 400; }
.landing-page nav.top .links a { position: relative; opacity: .8; transition: opacity .3s; cursor: pointer; user-select: none; }
.landing-page nav.top .links a:hover { opacity: 1; }
.landing-page nav.top .cta { justify-self: end; font-size: 13px; display: inline-flex; align-items: center; gap: 8px; padding: 9px 16px; border: 1px solid rgba(255,255,255,.7); border-radius: 999px; cursor: pointer; transition: all .3s; }
.landing-page nav.top .cta::after { content: "→"; }

/* HERO */
.landing-page .hero {
  position: relative; min-height: 100vh; min-height: 100dvh;
  display: grid; grid-template-rows: 1fr auto;
  color: white; overflow-x: hidden;
  background: #1a1612;
}
.landing-page .hero-bg { position: absolute; inset: 0; overflow: hidden; }
.landing-page .hero-bg img { width: 100%; height: 100%; object-fit: cover; }
.landing-page .hero-bg::after { content: ""; position: absolute; inset: 0; background: linear-gradient(180deg, rgba(20,17,13,.45) 0%, rgba(20,17,13,.15) 30%, rgba(20,17,13,.15) 50%, rgba(20,17,13,.65) 100%); }

.landing-page .hero-meta-top {
  position: absolute; top: 88px; left: 36px; right: 36px;
  display: flex; justify-content: space-between; gap: 36px;
  font-size: 12px; letter-spacing: .22em; text-transform: uppercase;
  color: rgba(255,255,255,.7); z-index: 2;
}
.landing-page .hero-meta-top span:nth-child(2) { letter-spacing: .05em; text-transform: none; font-family: var(--display); font-style: italic; font-size: 14px; color: rgba(255,255,255,.85); }

.landing-page .hero-content { position: relative; z-index: 2; align-self: end; padding: 0 36px 40px; }
.landing-page .hero h1 {
  font-family: var(--display); font-weight: 300;
  font-size: clamp(64px, 9vw, 160px);
  line-height: .88; letter-spacing: -.04em;
  font-variation-settings: "opsz" 144;
  text-wrap: balance;
  max-width: 1400px;
}
.landing-page .hero h1 em { font-style: italic; font-weight: 400; color: rgba(255,255,255,.95); }
.landing-page .hero h1 .indent { padding-left: 1.2em; }

.landing-page .hero-foot {
  position: relative; z-index: 2;
  display: grid; grid-template-columns: 1fr 1fr 1fr auto;
  align-items: end; gap: 24px;
  padding: 24px 48px 32px 36px;
  border-top: 1px solid rgba(255,255,255,.18);
}
.landing-page .hero-foot .col .l { font-size: 12px; letter-spacing: .2em; text-transform: uppercase; color: rgba(255,255,255,.55); display: block; margin-bottom: 6px; }
.landing-page .hero-foot .col .v { font-family: var(--display); font-weight: 400; font-style: italic; font-size: 20px; line-height: 1.1; }
.landing-page .hero-foot .actions { justify-self: end; display: flex; gap: 10px; flex-shrink: 0; flex-wrap: wrap; }
.landing-page .btn { white-space: nowrap; }

.landing-page .btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 14px 22px; font-size: 13px; font-weight: 500;
  letter-spacing: .02em; border-radius: 999px;
  cursor: pointer; border: 0; font-family: inherit; transition: transform .3s var(--ease), background .3s, color .3s;
}
.landing-page .btn:hover { transform: translateY(-1px); }
.landing-page .btn-primary { background: white; color: var(--ink); }
.landing-page .btn-primary::after { content: "→"; }
.landing-page .btn-primary:hover { background: var(--gold); }
.landing-page .btn-ghost { background: rgba(255,255,255,.08); color: white; border: 1px solid rgba(255,255,255,.7); }
.landing-page .btn-ghost:hover { border-color: white; background: rgba(255,255,255,.08); }
.landing-page .btn-dark { background: var(--ink); color: white; }
.landing-page .btn-dark::after { content: "→"; }
.landing-page .btn-dark:hover { background: var(--teal); }

/* Scroll cue — hidden, overlaps footer bar */
.landing-page .scroll-cue { display: none; }

/* STATEMENT */
.landing-page .statement {
  padding: 140px 36px 120px;
  max-width: 1500px; margin: 0 auto;
  display: grid; grid-template-columns: 1fr 1.4fr; gap: 80px; align-items: start;
}
.landing-page .statement aside { padding-top: 18px; }
.landing-page .statement aside .eyebrow { display: block; margin-bottom: 14px; color: var(--rust); }
.landing-page .statement aside p { font-family: var(--display); font-style: italic; font-weight: 300; font-size: 17px; color: var(--ink-2); line-height: 1.5; max-width: 260px; }
.landing-page .statement aside .index { margin-top: 24px; padding-top: 18px; border-top: 1px solid var(--ink); font-family: var(--sans); font-size: 12.5px; color: var(--ink-3); }
.landing-page .statement aside .index b { color: var(--ink); font-weight: 600; }

.landing-page .statement h2 {
  font-family: var(--display); font-weight: 300;
  font-size: clamp(48px, 6.5vw, 104px); line-height: .94; letter-spacing: -.025em;
  text-wrap: balance;
}
.landing-page .statement h2 em { font-style: italic; color: var(--teal); }

/* SPLIT FEATURE */
.landing-page .split {
  padding: 0 36px 120px;
  max-width: 1500px; margin: 0 auto;
  display: grid; grid-template-columns: 1.05fr 1fr;
  gap: 56px; align-items: center;
}
.landing-page .split.reverse > .split-art { order: 2; }
.landing-page .split-art {
  position: relative; aspect-ratio: 5/6; overflow: hidden; background: var(--cream);
}
.landing-page .split-art img { width: 100%; height: 100%; object-fit: cover; }
.landing-page .split-art .tag {
  position: absolute; top: 18px; left: 18px;
  font-size: 12px; letter-spacing: .18em; text-transform: uppercase; font-weight: 500;
  background: rgba(20,17,13,.55); color: white;
  backdrop-filter: blur(8px);
  padding: 7px 12px; border-radius: 999px;
}
.landing-page .split-art .cap {
  position: absolute; left: 18px; bottom: 18px;
  font-family: var(--display); font-style: italic; font-size: 15px; color: white; max-width: 240px; line-height: 1.3;
  text-shadow: 0 1px 4px rgba(0,0,0,.4);
}

.landing-page .split-text { padding-right: 24px; }
.landing-page .split-text .num { display: flex; align-items: baseline; gap: 12px; font-family: var(--display); font-style: italic; font-size: 17px; color: var(--rust); margin-bottom: 6px; }
.landing-page .split-text .num::after { content: ""; flex: 1; height: 1px; background: var(--ink); }
.landing-page .split-text .cat { display: block; margin-bottom: 30px; }
.landing-page .split-text h3 {
  font-family: var(--display); font-weight: 300;
  font-size: clamp(40px, 4.6vw, 72px); line-height: .96; letter-spacing: -.02em;
  text-wrap: balance;
  margin-bottom: 24px;
}
.landing-page .split-text h3 em { font-style: italic; color: var(--teal); }
.landing-page .split-text p { font-size: 17.5px; line-height: 1.55; color: var(--ink-2); max-width: 520px; margin-bottom: 14px; }
.landing-page .split-text p em { font-style: italic; }
.landing-page .split-text .stats { display: flex; gap: 48px; margin-top: 32px; padding-top: 24px; border-top: 1px solid var(--ink); max-width: 520px; }
.landing-page .split-text .stat .n { font-family: var(--display); font-weight: 300; font-size: 44px; line-height: 1; letter-spacing: -.02em; }
.landing-page .split-text .stat .n em { font-style: italic; color: var(--rust); }
.landing-page .split-text .stat .l { font-size: 12px; letter-spacing: .18em; text-transform: uppercase; color: var(--ink-3); margin-top: 6px; }

/* PRODUCT FRAME */
.landing-page .product {
  position: relative; padding: 140px 36px; background: var(--ink); color: white;
  overflow: hidden;
}
.landing-page .product-grid {
  max-width: 1500px; margin: 0 auto;
  display: grid; grid-template-columns: 1fr 1.05fr; gap: 64px; align-items: center;
}
.landing-page .product .eyebrow.lt { display: block; margin-bottom: 16px; color: var(--gold); }
.landing-page .product h3 {
  font-family: var(--display); font-weight: 300;
  font-size: clamp(44px, 5.5vw, 88px); line-height: .94; letter-spacing: -.025em;
  margin-bottom: 22px; text-wrap: balance;
}
.landing-page .product h3 em { font-style: italic; color: var(--gold); }
.landing-page .product .body { font-size: 17.5px; line-height: 1.55; color: rgba(246,241,230,.78); max-width: 460px; margin-bottom: 24px; }
.landing-page .product .body em { font-style: italic; color: var(--cream); }

/* Mock UI */
.landing-page .mock {
  background: var(--paper); color: var(--ink);
  border-radius: 18px; padding: 18px;
  box-shadow: 0 60px 80px -30px rgba(0,0,0,.55), 0 20px 30px -10px rgba(0,0,0,.3);
  font-family: var(--sans);
}
.landing-page .mock-head { display: flex; justify-content: space-between; align-items: center; padding: 6px 8px 14px; border-bottom: 1px solid #e6dccb; }
.landing-page .mock-head .h-l { display: flex; align-items: center; gap: 10px; }
.landing-page .mock-head .h-l h4 { font-family: var(--display); font-weight: 400; font-style: italic; font-size: 18px; }
.landing-page .mock-head .ai { display: inline-flex; align-items: center; gap: 6px; background: rgba(212,162,76,.18); color: #b8842a; font-size: 10.5px; font-weight: 600; letter-spacing: .04em; text-transform: uppercase; padding: 5px 10px; border-radius: 999px; }
.landing-page .mock-head .ai::before { content: ""; width: 6px; height: 6px; border-radius: 50%; background: currentColor; animation: pulse 1.4s ease-in-out infinite; }
@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: .35; } }

.landing-page .mock-row { display: grid; grid-template-columns: 40px 1fr auto auto; gap: 14px; align-items: center; padding: 14px 8px; border-bottom: 1px solid #efe6d3; }
.landing-page .mock-row:last-child { border-bottom: 0; }
.landing-page .mock-row .av { width: 40px; height: 40px; border-radius: 50%; background: var(--teal); color: white; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 600; font-family: var(--sans); }
.landing-page .mock-row .name { font-size: 14px; font-weight: 600; }
.landing-page .mock-row .sub { font-size: 12.5px; color: var(--ink-3); margin-top: 1px; }
.landing-page .mock-row .pill { padding: 4px 10px; font-size: 10.5px; letter-spacing: .04em; text-transform: uppercase; font-weight: 600; border-radius: 999px; }
.landing-page .mock-row .pill.ok { background: rgba(76,140,90,.16); color: #4c8c5a; }
.landing-page .mock-row .pill.pend { background: rgba(212,162,76,.2); color: #b8842a; }
.landing-page .mock-row .pill.dec { background: rgba(217,107,94,.18); color: #c25446; }
.landing-page .mock-row .ts { font-size: 11.5px; color: var(--ink-3); }

/* FULL-BLEED PHOTO QUOTE */
.landing-page .bleed {
  position: relative; height: 92vh; min-height: 640px; overflow: hidden;
  color: white;
}
.landing-page .bleed > img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.landing-page .bleed::after { content: ""; position: absolute; inset: 0; background: linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,.45) 100%); }
.landing-page .bleed-content { position: absolute; left: 36px; right: 36px; bottom: 56px; z-index: 2; }
.landing-page .bleed h2 {
  font-family: var(--display); font-weight: 300;
  font-size: clamp(48px, 7vw, 120px); line-height: .92; letter-spacing: -.025em;
  max-width: 1200px;
}
.landing-page .bleed h2 em { font-style: italic; }
.landing-page .bleed-foot { margin-top: 28px; display: flex; justify-content: space-between; align-items: end; gap: 40px; flex-wrap: wrap; }
.landing-page .bleed-foot .who { font-family: var(--display); font-style: italic; font-size: 19px; color: rgba(255,255,255,.85); }
.landing-page .bleed-foot .who::before { content: "— "; }
.landing-page .bleed-foot .credit { font-size: 12px; letter-spacing: .22em; text-transform: uppercase; color: rgba(255,255,255,.55); }

/* GALLERY */
.landing-page .gallery {
  padding: 140px 36px;
  max-width: 1500px; margin: 0 auto;
}
.landing-page .gallery-head { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; margin-bottom: 64px; align-items: end; }
.landing-page .gallery-head h2 { font-family: var(--display); font-weight: 300; font-size: clamp(48px, 6vw, 92px); line-height: .94; letter-spacing: -.025em; text-wrap: balance; }
.landing-page .gallery-head h2 em { font-style: italic; }
.landing-page .gallery-head .desc { font-family: var(--display); font-style: italic; font-size: 18px; color: var(--ink-3); line-height: 1.45; max-width: 380px; padding-bottom: 10px; }

.landing-page .g-grid {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-auto-rows: 80px;
  gap: 18px;
}
.landing-page .g-item { position: relative; overflow: hidden; background: var(--cream); }
.landing-page .g-item img { width: 100%; height: 100%; object-fit: cover; }
.landing-page .g-item .cap {
  position: absolute; left: 14px; right: 14px; bottom: 14px;
  font-family: var(--display); font-style: italic; font-size: 14px; color: white;
  text-shadow: 0 1px 3px rgba(0,0,0,.45);
  display: flex; justify-content: space-between; align-items: end; gap: 12px;
}
.landing-page .g-item .cap b { font-style: normal; font-family: var(--sans); font-size: 11.5px; letter-spacing: .22em; text-transform: uppercase; opacity: .8; }
.landing-page .g-1 { grid-column: 1 / 6; grid-row: 1 / 6; }
.landing-page .g-2 { grid-column: 6 / 10; grid-row: 1 / 5; }
.landing-page .g-3 { grid-column: 10 / 13; grid-row: 1 / 4; }
.landing-page .g-4 { grid-column: 6 / 10; grid-row: 5 / 9; }
.landing-page .g-5 { grid-column: 1 / 6; grid-row: 6 / 10; }
.landing-page .g-6 { grid-column: 10 / 13; grid-row: 4 / 9; }

/* VOICES */
.landing-page .voices {
  padding: 140px 36px;
  background: var(--cream);
  border-top: 1px solid rgba(20,17,13,.1);
  border-bottom: 1px solid rgba(20,17,13,.1);
}
.landing-page .voices-wrap { max-width: 1500px; margin: 0 auto; }
.landing-page .voices-head { display: flex; justify-content: space-between; align-items: end; padding-bottom: 36px; border-bottom: 1px solid var(--ink); margin-bottom: 64px; gap: 40px; flex-wrap: wrap; }
.landing-page .voices-head h2 { font-family: var(--display); font-weight: 300; font-size: clamp(40px, 5vw, 76px); line-height: .96; letter-spacing: -.02em; max-width: 880px; }
.landing-page .voices-head h2 em { font-style: italic; }
.landing-page .voices-head .eyebrow { color: var(--rust); }
.landing-page .voices-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 36px; }
.landing-page .voice { display: flex; flex-direction: column; gap: 18px; }
.landing-page .voice-img { aspect-ratio: 4/5; overflow: hidden; }
.landing-page .voice-img img { width: 100%; height: 100%; object-fit: cover; }
.landing-page .voice .role { font-size: 12px; letter-spacing: .22em; text-transform: uppercase; color: var(--rust); }
.landing-page .voice .quote { font-family: var(--display); font-weight: 300; font-size: 22px; line-height: 1.3; letter-spacing: -.005em; text-wrap: balance; }
.landing-page .voice .quote em { font-style: italic; color: var(--teal); }
.landing-page .voice .who { font-size: 13px; font-style: italic; color: var(--ink-3); }

/* PRICING */
.landing-page .pricing {
  padding: 140px 36px;
  max-width: 1500px; margin: 0 auto;
}
.landing-page .pricing-head { text-align: center; margin-bottom: 64px; }
.landing-page .pricing-head h2 { font-family: var(--display); font-weight: 300; font-size: clamp(48px, 6vw, 92px); line-height: .94; letter-spacing: -.025em; margin-bottom: 20px; }
.landing-page .pricing-head h2 em { font-style: italic; color: var(--teal); }
.landing-page .pricing-head p { font-family: var(--display); font-style: italic; font-size: 18px; color: var(--ink-3); max-width: 480px; margin: 0 auto; }
.landing-page .pricing-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
.landing-page .price-card {
  background: white; border-radius: 24px; padding: 40px 32px;
  border: 1px solid rgba(20,17,13,.1);
  display: flex; flex-direction: column;
  transition: transform .3s var(--ease), box-shadow .3s var(--ease);
}
.landing-page .price-card:hover { transform: translateY(-4px); box-shadow: 0 20px 60px rgba(0,0,0,.1); }
.landing-page .price-card.featured {
  background: var(--teal); color: white; border-color: var(--teal);
  position: relative;
  transform: scale(1.04);
  box-shadow: 0 24px 80px rgba(21,78,74,.3);
  z-index: 2;
}
.landing-page .price-card.featured:hover { transform: scale(1.04) translateY(-4px); box-shadow: 0 32px 100px rgba(21,78,74,.4); }
.landing-page .price-card.featured::before {
  content: "Most Popular"; position: absolute; top: -14px; left: 50%; transform: translateX(-50%);
  background: var(--gold); color: white; font-size: 10.5px; font-weight: 600;
  letter-spacing: .12em; text-transform: uppercase; padding: 7px 20px; border-radius: 999px;
  box-shadow: 0 4px 12px rgba(181,138,55,.3);
}
.landing-page .price-card .tier { font-size: 12px; letter-spacing: .22em; text-transform: uppercase; font-weight: 500; color: var(--ink-3); margin-bottom: 8px; }
.landing-page .price-card.featured .tier { color: rgba(255,255,255,.6); }
.landing-page .price-card .price { font-family: var(--display); font-weight: 300; font-size: 56px; line-height: 1; letter-spacing: -.02em; margin-bottom: 4px; }
.landing-page .price-card .price sup { font-size: 24px; vertical-align: super; }
.landing-page .price-card .period { font-size: 14px; color: var(--ink-3); margin-bottom: 24px; }
.landing-page .price-card.featured .period { color: rgba(255,255,255,.6); }
.landing-page .price-card .limits { display: flex; gap: 24px; padding: 24px 0; border-top: 1px solid rgba(20,17,13,.1); border-bottom: 1px solid rgba(20,17,13,.1); margin-bottom: 24px; }
.landing-page .price-card.featured .limits { border-color: rgba(255,255,255,.2); }
.landing-page .price-card .limit { flex: 1; }
.landing-page .price-card .limit .n { font-family: var(--display); font-weight: 400; font-size: 28px; line-height: 1; }
.landing-page .price-card .limit .l { font-size: 12px; color: var(--ink-3); margin-top: 4px; }
.landing-page .price-card.featured .limit .l { color: rgba(255,255,255,.6); }
.landing-page .price-card ul { list-style: none; flex: 1; margin-bottom: 32px; }
.landing-page .price-card li { font-size: 14px; color: var(--ink-2); padding: 8px 0; display: flex; align-items: center; gap: 10px; }
.landing-page .price-card.featured li { color: rgba(255,255,255,.85); }
.landing-page .price-card li::before { content: "✓"; color: var(--teal); font-weight: 600; }
.landing-page .price-card.featured li::before { color: var(--gold); }
.landing-page .price-card .btn-price {
  display: block; text-align: center; padding: 16px 24px; border-radius: 999px;
  font-size: 14px; font-weight: 500; cursor: pointer; transition: all .3s var(--ease);
  background: var(--ink); color: white; border: none;
}
.landing-page .price-card .btn-price:hover { background: var(--teal); transform: translateY(-1px); }
.landing-page .price-card.featured .btn-price { background: white; color: var(--teal); }
.landing-page .price-card.featured .btn-price:hover { background: var(--gold); color: white; }

@media (max-width: 900px) {
  .landing-page .pricing { padding: 80px 20px; }
  .landing-page .pricing-grid { grid-template-columns: 1fr; gap: 20px; }
  .landing-page .price-card.featured { order: -1; }
}

/* INVITE (closer) */
.landing-page section.invite {
  position: relative;
  height: 96vh; min-height: 640px; overflow: hidden;
  color: white;
}
.landing-page section.invite > img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.landing-page section.invite::after { content: ""; position: absolute; inset: 0; background: linear-gradient(180deg, rgba(11,52,49,.2) 0%, rgba(11,52,49,.6) 50%, rgba(11,52,49,.95) 100%); }
.landing-page .cta-content { position: absolute; inset: 0; z-index: 2; display: flex; flex-direction: column; justify-content: space-between; padding: 96px 36px 48px; }
.landing-page .cta-top { text-align: center; }
.landing-page .cta-top .eyebrow { color: rgba(246,241,230,.65); display: block; margin-bottom: 18px; }
.landing-page .cta-top h2 {
  font-family: var(--display); font-weight: 300;
  font-size: clamp(64px, 11vw, 200px); line-height: .86; letter-spacing: -.04em;
  text-wrap: balance;
}
.landing-page .cta-top h2 em { font-style: italic; color: var(--gold); }
.landing-page .cta-actions { display: flex; gap: 12px; justify-content: center; margin-top: 36px; flex-wrap: wrap; }
.landing-page .cta-foot { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; padding-top: 24px; border-top: 1px solid rgba(255,255,255,.2); }
.landing-page .cta-foot .col .l { font-size: 12px; letter-spacing: .2em; text-transform: uppercase; color: rgba(255,255,255,.65); display: block; margin-bottom: 6px; }
.landing-page .cta-foot .col .v { font-family: var(--display); font-style: italic; font-size: 18px; color: white; font-weight: 400; }
.landing-page .cta-foot .col:last-child { text-align: right; }
.landing-page .cta-foot .col:last-child .v { font-family: var(--display); font-weight: 400; font-size: 22px; letter-spacing: -.01em; }
.landing-page .cta-foot .col:last-child .v em { font-style: italic; }

/* FOOTER */
.landing-page footer.colophon {
  background: var(--teal-deep); color: rgba(246,241,230,.7);
  padding: 56px 36px 32px;
  display: grid; grid-template-columns: 1.4fr 1fr 1fr 1fr; gap: 40px;
}
.landing-page footer.colophon .brand-mark { font-family: var(--display); font-weight: 300; font-size: 42px; letter-spacing: -.02em; color: white; margin-bottom: 10px; }
.landing-page footer.colophon .brand-mark em { font-style: italic; color: var(--gold); }
.landing-page footer.colophon .mission { font-family: var(--display); font-style: italic; font-size: 17px; line-height: 1.45; max-width: 360px; color: rgba(246,241,230,.7); }
.landing-page footer.colophon h4 { font-size: 12px; letter-spacing: .22em; text-transform: uppercase; color: var(--gold); margin-bottom: 14px; }
.landing-page footer.colophon ul { list-style: none; }
.landing-page footer.colophon li { font-size: 14px; margin-bottom: 9px; }
.landing-page footer.colophon li a { cursor: pointer; }
.landing-page footer.colophon li a:hover { color: white; }
.landing-page footer.colophon .small { grid-column: 1 / 5; margin-top: 36px; padding-top: 18px; border-top: 1px solid rgba(246,241,230,.16); font-size: 12px; letter-spacing: .18em; text-transform: uppercase; color: rgba(246,241,230,.45); display: flex; justify-content: space-between; flex-wrap: wrap; gap: 14px; }

/* MOBILE MENU */
.landing-page .hamburger {
  display: none; background: none; border: none; cursor: pointer;
  width: 32px; height: 24px; position: relative; padding: 0;
}
.landing-page .hamburger span {
  display: block; position: absolute; left: 0; width: 100%; height: 2px;
  background: currentColor; border-radius: 2px;
  transition: transform .35s var(--ease), opacity .25s;
}
.landing-page .hamburger span:nth-child(1) { top: 2px; }
.landing-page .hamburger span:nth-child(2) { top: 11px; }
.landing-page .hamburger span:nth-child(3) { top: 20px; }
.landing-page .hamburger.open span:nth-child(1) { top: 11px; transform: rotate(45deg); }
.landing-page .hamburger.open span:nth-child(2) { opacity: 0; }
.landing-page .hamburger.open span:nth-child(3) { top: 11px; transform: rotate(-45deg); }

.landing-page .mobile-drawer {
  position: fixed; inset: 0; z-index: 49;
  background: rgba(246,241,230,.97);
  -webkit-backdrop-filter: saturate(140%) blur(20px);
  backdrop-filter: saturate(140%) blur(20px);
  display: flex; flex-direction: column; justify-content: center; align-items: center; gap: 8px;
  opacity: 0; pointer-events: none;
  transition: opacity .4s var(--ease);
}
.landing-page .mobile-drawer.open { opacity: 1; pointer-events: auto; }
.landing-page .mobile-drawer a {
  font-family: var(--display); font-weight: 300; font-size: 40px;
  line-height: 1.4; letter-spacing: -.02em; color: var(--ink);
  cursor: pointer; transition: color .2s;
}
.landing-page .mobile-drawer a:hover { color: var(--teal); }
.landing-page .mobile-drawer .drawer-cta {
  margin-top: 24px; display: inline-flex; align-items: center; gap: 8px;
  padding: 16px 28px; font-size: 15px; font-weight: 500; font-family: var(--sans);
  background: var(--ink); color: white; border: none; border-radius: 999px; cursor: pointer;
  transition: background .3s var(--ease);
}
.landing-page .mobile-drawer .drawer-cta::after { content: "→"; }
.landing-page .mobile-drawer .drawer-cta:hover { background: var(--teal); }

/* Responsive */
@media (max-width: 900px) {
  .landing-page nav.top { padding: 16px 20px; grid-template-columns: 1fr auto auto; gap: 12px; }
  .landing-page nav.top .links { display: none; }
  .landing-page .hamburger { display: block; }
  .landing-page .hero-meta-top { top: 70px; left: 20px; right: 20px; flex-direction: column; gap: 8px; }
  .landing-page .hero-content { padding: 0 20px 32px; }
  .landing-page .hero h1 { font-size: 12vw; }
  .landing-page .hero-foot { grid-template-columns: 1fr 1fr; padding: 18px 20px 24px; gap: 16px; }
  .landing-page .hero-foot .actions { grid-column: 1 / 3; justify-self: stretch; }
  .landing-page .statement { grid-template-columns: 1fr; gap: 32px; padding: 80px 20px; }
  .landing-page .split, .landing-page .split.reverse { grid-template-columns: 1fr; gap: 32px; padding: 0 20px 64px; }
  .landing-page .split.reverse .split-art { order: 0; }
  .landing-page .split-text { padding-right: 0; }
  .landing-page .product, .landing-page .gallery, .landing-page .voices { padding: 80px 20px; }
  .landing-page .product-grid { grid-template-columns: 1fr; gap: 32px; }
  .landing-page .gallery-head { grid-template-columns: 1fr; gap: 16px; margin-bottom: 32px; }
  .landing-page .g-grid { grid-template-columns: 1fr; grid-auto-rows: 60vw; gap: 14px; }
  .landing-page .g-1, .landing-page .g-2, .landing-page .g-3, .landing-page .g-4, .landing-page .g-5, .landing-page .g-6 { grid-column: 1; grid-row: auto; }
  .landing-page .voices-grid { grid-template-columns: 1fr; gap: 32px; }
  .landing-page .bleed-content { left: 20px; right: 20px; bottom: 36px; }
  .landing-page .cta-content { padding: 80px 20px 36px; }
  .landing-page .cta-foot { grid-template-columns: 1fr 1fr; }
  .landing-page footer.colophon { grid-template-columns: 1fr 1fr; padding: 40px 20px 28px; gap: 28px; }
  .landing-page footer.colophon .small { grid-column: 1 / 3; flex-direction: column; }
}

/* Small phones */
@media (max-width: 480px) {
  .landing-page nav.top { padding: 14px 16px; grid-template-columns: 1fr auto auto; }
  .landing-page nav.top .cta { padding: 8px 14px; font-size: 12px; }
  .landing-page nav.top.scrolled { padding: 10px 16px; }

  .landing-page .hero-meta-top { top: 60px; left: 16px; right: 16px; gap: 4px; }
  .landing-page .hero-meta-top span { font-size: 9px; }
  .landing-page .hero-meta-top span:nth-child(2) { font-size: 12px; }
  .landing-page .hero-meta-top span:last-child { display: none; }

  .landing-page .hero-content { padding: 0 16px 24px; }
  .landing-page .hero h1 { font-size: 14vw; }
  .landing-page .hero h1 .indent { padding-left: .6em; }

  .landing-page .hero-foot { grid-template-columns: 1fr; padding: 16px 16px 20px; gap: 12px; }
  .landing-page .hero-foot .col .v { font-size: 16px; }
  .landing-page .hero-foot .actions { grid-column: 1; flex-direction: column; }
  .landing-page .hero-foot .actions .btn { width: 100%; justify-content: center; padding: 12px 18px; }

  .landing-page .scroll-cue { display: none; }

  .landing-page .statement { padding: 56px 16px; gap: 24px; }
  .landing-page .statement h2 { font-size: clamp(36px, 10vw, 48px); }
  .landing-page .statement aside p { font-size: 15px; }

  .landing-page .split, .landing-page .split.reverse { padding: 0 16px 48px; gap: 24px; }
  .landing-page .split-text h3 { font-size: clamp(32px, 9vw, 40px); margin-bottom: 16px; }
  .landing-page .split-text p { font-size: 15px; }
  .landing-page .split-text .stats { gap: 32px; margin-top: 24px; padding-top: 18px; }
  .landing-page .split-text .stat .n { font-size: 36px; }

  .landing-page .product { padding: 56px 16px; }
  .landing-page .product h3 { font-size: clamp(36px, 10vw, 44px); }
  .landing-page .product .body { font-size: 15px; }
  .landing-page .mock { padding: 12px; border-radius: 14px; }
  .landing-page .mock-head .h-l h4 { font-size: 15px; }
  .landing-page .mock-row { grid-template-columns: 32px 1fr auto; gap: 10px; padding: 10px 6px; }
  .landing-page .mock-row .av { width: 32px; height: 32px; font-size: 11px; }
  .landing-page .mock-row .name { font-size: 13px; }
  .landing-page .mock-row .sub { font-size: 11.5px; }
  .landing-page .mock-row .ts { display: none; }

  .landing-page .bleed { height: 70vh; min-height: 480px; }
  .landing-page .bleed-content { left: 16px; right: 16px; bottom: 24px; }
  .landing-page .bleed h2 { font-size: clamp(32px, 9vw, 48px); }
  .landing-page .bleed-foot .who { font-size: 15px; }

  .landing-page .gallery { padding: 56px 16px; }
  .landing-page .gallery-head { margin-bottom: 24px; }
  .landing-page .gallery-head h2 { font-size: clamp(36px, 10vw, 48px); }
  .landing-page .gallery-head .desc { font-size: 15px; }
  .landing-page .g-grid { gap: 10px; grid-auto-rows: 56vw; }

  .landing-page .voices { padding: 56px 16px; }
  .landing-page .voices-head { margin-bottom: 40px; padding-bottom: 24px; }
  .landing-page .voices-head h2 { font-size: clamp(32px, 8vw, 40px); }
  .landing-page .voice .quote { font-size: 18px; }

  .landing-page .pricing { padding: 56px 16px; }
  .landing-page .pricing-head h2 { font-size: clamp(36px, 10vw, 48px); }
  .landing-page .pricing-head p { font-size: 15px; }
  .landing-page .price-card { padding: 28px 22px; border-radius: 18px; }
  .landing-page .price-card .price { font-size: 44px; }

  .landing-page section.invite { height: 80vh; min-height: 540px; }
  .landing-page .cta-content { padding: 60px 16px 28px; }
  .landing-page .cta-top h2 { font-size: clamp(48px, 14vw, 64px); }
  .landing-page .cta-foot { grid-template-columns: 1fr 1fr; gap: 16px; padding-top: 18px; }
  .landing-page .cta-foot .col .v { font-size: 15px; }
  .landing-page .cta-foot .col:last-child .v { font-size: 18px; }

  .landing-page footer.colophon { grid-template-columns: 1fr; padding: 32px 16px 24px; gap: 24px; }
  .landing-page footer.colophon .brand-mark { font-size: 32px; }
  .landing-page footer.colophon .mission { font-size: 15px; }
  .landing-page footer.colophon .small { grid-column: 1; margin-top: 24px; }
}
`;

const LandingPage = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);

  // Scroll handling for nav
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Intersection observer for animations
  useEffect(() => {
    const sections = document.querySelectorAll('[data-anim]');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const root = e.target;
        root.querySelectorAll('.line').forEach((el) => el.classList.add('in'));
        root.querySelectorAll('[data-anim-target]').forEach((el) => el.classList.add('in'));
        io.unobserve(root);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    sections.forEach((s) => io.observe(s));

    // Trigger hero immediately
    requestAnimationFrame(() => {
      const hero = document.querySelector('.hero[data-anim]');
      if (hero) {
        hero.querySelectorAll('.line').forEach((el) => el.classList.add('in'));
        hero.querySelectorAll('[data-anim-target]').forEach((el) => el.classList.add('in'));
      }
    });

    return () => io.disconnect();
  }, []);

  // Smart navigation
  const handleGetStarted = useCallback(() => {
    if (authService.isAuthenticated()) {
      navigate('/events');
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* Google Fonts */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300..600;1,9..144,300..600&family=Inter:wght@300;400;500;600&display=swap" rel="stylesheet" />

      <style>{styles}</style>

      <div className="landing-page">
        {/* NAV */}
        <nav className={`top ${scrolled ? 'scrolled' : ''}`}>
          <div className="brand">Mandap<em>.</em></div>
          <div className="links">
            <a onClick={() => scrollToSection('statement')}>Approach</a>
            <a onClick={() => scrollToSection('feature-rsvp')}>Features</a>
            <a onClick={() => scrollToSection('pricing')}>Pricing</a>
            <a onClick={() => scrollToSection('voices')}>Voices</a>
          </div>
          <button className={`hamburger ${menuOpen ? 'open' : ''}`} onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
            <span /><span /><span />
          </button>
          <a onClick={handleGetStarted} className="cta">Begin</a>
        </nav>

        {/* MOBILE DRAWER */}
        <div className={`mobile-drawer ${menuOpen ? 'open' : ''}`}>
          <a onClick={() => { scrollToSection('statement'); setMenuOpen(false); }}>Approach</a>
          <a onClick={() => { scrollToSection('feature-rsvp'); setMenuOpen(false); }}>Features</a>
          <a onClick={() => { scrollToSection('pricing'); setMenuOpen(false); }}>Pricing</a>
          <a onClick={() => { scrollToSection('voices'); setMenuOpen(false); }}>Voices</a>
          <button className="drawer-cta" onClick={() => { handleGetStarted(); setMenuOpen(false); }}>Begin</button>
        </div>

        {/* HERO */}
        <section className="hero" data-anim>
          <div className="hero-bg">
            <img className="kenburns" data-anim-target src="https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=2400&q=85" alt="Wedding mandap with marigolds" />
          </div>
          <div className="hero-meta-top">
            <span>Mandap <span style={{color:'rgba(255,255,255,.4)'}}>·</span> A planner for hosts</span>
            <span>Your guests, handled — from RSVP to goodbye</span>
          </div>
          <div className="hero-content">
            <h1>
              <span className="line"><span>The wedding,</span></span>
              <span className="line d1"><span><em>handled</em> —</span></span>
              <span className="line d2 indent"><span>so you can be there.</span></span>
            </h1>
          </div>
          <div className="hero-foot fade d3" data-anim-target>
            <div className="col"><span className="l">For</span><span className="v">Hosts &amp; planners</span></div>
            <div className="col"><span className="l">Replies</span><span className="v">In your name</span></div>
            <div className="col"><span className="l">Built around</span><span className="v">312 guests · 0 chases</span></div>
            <div className="actions">
              <button onClick={handleGetStarted} className="btn btn-primary">Begin an event</button>
              <button onClick={() => scrollToSection('product')} className="btn btn-ghost">See how it works</button>
            </div>
          </div>
          <div className="scroll-cue">Scroll</div>
        </section>

        {/* STATEMENT */}
        <section className="statement" id="statement" data-anim>
          <aside>
            <span className="eyebrow fade" data-anim-target>Approach · 2026</span>
            <p className="fade d1" data-anim-target>A quiet concierge for the noisiest event of your life. We answer the questions, send the reminders, and mark the RSVPs.</p>
            <div className="index fade d2" data-anim-target>
              <b>01</b> RSVPs &nbsp; · &nbsp; <b>02</b> FAQ Inbox &nbsp; · &nbsp; <b>03</b> Messages
            </div>
          </aside>
          <h2>
            <span className="line"><span>A wedding has</span></span>
            <span className="line d1"><span><em>a thousand</em> small</span></span>
            <span className="line d2"><span>questions.</span></span>
            <span className="line d3"><span>We answer</span></span>
            <span className="line d4"><span><em>all of them.</em></span></span>
          </h2>
        </section>

        {/* FEATURE 01 — RSVPs */}
        <section className="split" id="feature-rsvp" data-anim>
          <div className="split-art reveal" data-anim-target>
            <span className="tag">Feature 01 · RSVPs</span>
            <img src="https://images.unsplash.com/photo-1519741497674-611481863552?w=1600&q=85" alt="Bride's hands holding marigold petals" />
            <div className="cap">Photographed the morning <em>everyone</em> finally replied.</div>
          </div>
          <div className="split-text">
            <div className="num fade" data-anim-target>01</div>
            <span className="cat eyebrow fade d1" data-anim-target>The RSVP, reimagined</span>
            <h3>
              <span className="line"><span>A guest list that</span></span>
              <span className="line d1"><span><em>fills itself in</em></span></span>
              <span className="line d2"><span>while you sleep.</span></span>
            </h3>
            <p className="fade d3" data-anim-target>Mandap reads <em>"Yes, both of us"</em> and marks it. Reads <em>"We can't, so sorry,"</em> and marks that too. Hindi, Gujarati, English, a sticker, a voice note — whatever your aunt sends at midnight, the list is current by morning.</p>
            <p className="fade d4" data-anim-target>You never chase a single reply. Guests confirm on their own terms — and you wake up to a list that's already current.</p>
            <div className="stats fade d5" data-anim-target>
              <div className="stat">
                <div className="n"><em>247</em>/312</div>
                <div className="l">Confirmed without a chase</div>
              </div>
              <div className="stat">
                <div className="n">11<em>s</em></div>
                <div className="l">Median reply time</div>
              </div>
            </div>
          </div>
        </section>

        {/* PRODUCT FRAME — FAQ INBOX */}
        <section className="product" id="product" data-anim>
          <div className="product-grid">
            <div>
              <span className="eyebrow lt fade" data-anim-target>Feature 02 · The FAQ inbox</span>
              <h3>
                <span className="line"><span>An answer for</span></span>
                <span className="line d1"><span><em>every 4 a.m.</em></span></span>
                <span className="line d2"><span>question.</span></span>
              </h3>
              <p className="body fade d3" data-anim-target>Write the FAQ once. Parking, dress code, gifts, dietary needs, accommodation, the bus to the mandap — answered in the words your guests <em>actually use,</em> the moment they ask.</p>
              <button onClick={() => scrollToSection('invite')} className="btn btn-primary fade d4" data-anim-target style={{marginTop:'8px'}}>Book a demo</button>
            </div>
            <div className="mock fade d2" data-anim-target>
              <div className="mock-head">
                <div className="h-l"><h4>Riddhi's family · WhatsApp</h4></div>
                <span className="ai">AI auto-reply</span>
              </div>
              <div className="mock-row">
                <div className="av">PA</div>
                <div><div className="name">Priya Auntie</div><div className="sub">Hi beta, will there be valet at the venue?</div></div>
                <div className="pill ok">Answered</div>
                <div className="ts">14s</div>
              </div>
              <div className="mock-row">
                <div className="av" style={{background:'var(--rust)'}}>RM</div>
                <div><div className="name">Rohan Mehta</div><div className="sub">Dress code for the Sangeet?</div></div>
                <div className="pill ok">Answered</div>
                <div className="ts">9s</div>
              </div>
              <div className="mock-row">
                <div className="av" style={{background:'var(--gold)'}}>SK</div>
                <div><div className="name">Sanjay Kapoor</div><div className="sub">Can I bring my partner?</div></div>
                <div className="pill pend">Tentative</div>
                <div className="ts">just now</div>
              </div>
              <div className="mock-row">
                <div className="av">MA</div>
                <div><div className="name">Meera Auntie</div><div className="sub">Sorry beta, we can't make it 😔</div></div>
                <div className="pill dec">Declined</div>
                <div className="ts">3m</div>
              </div>
            </div>
          </div>
        </section>

        {/* FULL-BLEED PHOTO QUOTE */}
        <section className="bleed" data-anim>
          <img className="kenburns" data-anim-target src="https://images.unsplash.com/photo-1469371670807-013ccf25f16a?w=2400&q=85" alt="Wedding ceremony aisle" />
          <div className="bleed-content">
            <h2>
              <span className="line"><span>I read a <em>book</em></span></span>
              <span className="line d1"><span>on the flight to a wedding —</span></span>
              <span className="line d2"><span>for the first time in seven seasons.</span></span>
            </h2>
            <div className="bleed-foot fade d3" data-anim-target>
              <div className="who">Sneha Iyer, planner · Cinnamon Events</div>
              <div className="credit">Issue 01 · Voice 02</div>
            </div>
          </div>
        </section>

        {/* FEATURE 03 — MESSAGES */}
        <section className="split reverse" id="feature-messages" data-anim>
          <div className="split-art reveal" data-anim-target>
            <span className="tag">Feature 03 · Messages</span>
            <img src="https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=1600&q=85" alt="Bride writing in journal" />
            <div className="cap">The card she sent to <em>everyone</em> — written once, at noon.</div>
          </div>
          <div className="split-text">
            <div className="num fade" data-anim-target>03</div>
            <span className="cat eyebrow fade d1" data-anim-target>Scheduled messages</span>
            <h3>
              <span className="line"><span>Write once.</span></span>
              <span className="line d1"><span>Arrive on time,</span></span>
              <span className="line d2"><span><em>everywhere.</em></span></span>
            </h3>
            <p className="fade d3" data-anim-target>Save-the-dates. Tonight's mehendi address. The bus number to the venue. Send to <em>family-only,</em> <em>college friends,</em> or <em>everyone arriving Saturday.</em> Mandap remembers who needs what.</p>
            <p className="fade d4" data-anim-target>You write the kind note. Mandap delivers it, on time, in their language, every time.</p>
            <div className="stats fade d5" data-anim-target>
              <div className="stat">
                <div className="n"><em>11</em></div>
                <div className="l">Languages, auto-translated</div>
              </div>
              <div className="stat">
                <div className="n">04</div>
                <div className="l">Channels — one send</div>
              </div>
            </div>
          </div>
        </section>

        {/* GALLERY */}
        <section className="gallery" id="gallery" data-anim>
          <header className="gallery-head">
            <h2>
              <span className="line"><span>Real weddings,</span></span>
              <span className="line d1"><span><em>quietly</em> handled.</span></span>
            </h2>
            <p className="desc fade d2" data-anim-target>A small portfolio of the events Mandap has held this season — and what its hosts were doing while it answered the chats.</p>
          </header>
          <div className="g-grid">
            <div className="g-item g-1 reveal" data-anim-target>
              <img src="/wedding-photography/ACA01046-scaled.jpeg" alt="Couple embracing in redwood forest" />
              <div className="cap"><span>Between the trees, after the vows.</span><b>01</b></div>
            </div>
            <div className="g-item g-2 reveal d1" data-anim-target>
              <img src="/wedding-photography/ACA03053-scaled.jpg" alt="Reception tables with string lights" />
              <div className="cap"><span>312 confirmed, 0 chases.</span><b>02</b></div>
            </div>
            <div className="g-item g-3 reveal d2" data-anim-target>
              <img src="/wedding-photography/ACA01509-scaled.jpeg" alt="Bride with bridesmaids laughing" />
              <div className="cap"><span>The bridal party, finally <em>relaxed.</em></span><b>03</b></div>
            </div>
            <div className="g-item g-4 reveal d1" data-anim-target>
              <img src="/wedding-photography/NatalieMichael_WEDDING-621-scaled.jpg" alt="Four-tier wedding cake with flowers" />
              <div className="cap"><span>The cake arrived on time.</span><b>04</b></div>
            </div>
            <div className="g-item g-5 reveal d2" data-anim-target>
              <img src="/wedding-photography/onf-25.jpg" alt="Groomsmen dancing at reception" />
              <div className="cap"><span>Sangeet night, on time.</span><b>05</b></div>
            </div>
            <div className="g-item g-6 reveal d3" data-anim-target>
              <img src="https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&q=85" alt="Hands &amp; marigolds" />
              <div className="cap"><span>The aunt who finally replied.</span><b>06</b></div>
            </div>
          </div>
        </section>

        {/* VOICES */}
        <section className="voices" id="voices" data-anim>
          <div className="voices-wrap">
            <header className="voices-head">
              <h2>
                <span className="line"><span>Three planners,</span></span>
                <span className="line d1"><span><em>three brides,</em> one</span></span>
                <span className="line d2"><span>very quiet wedding season.</span></span>
              </h2>
              <span className="eyebrow fade d3" data-anim-target>Voices · Wedding season 2026</span>
            </header>
            <div className="voices-grid">
              <article className="voice fade d1" data-anim-target>
                <div className="voice-img reveal" data-anim-target><img src="https://images.unsplash.com/photo-1525772764200-be829a350797?w=1200&q=85" alt="Priya Agarwal" /></div>
                <span className="role">The bride · April</span>
                <p className="quote">My mother stopped <em>calling me about parking.</em> That alone was worth it.</p>
                <div className="who">Priya Agarwal — Bombay · 312 guests</div>
              </article>
              <article className="voice fade d2" data-anim-target>
                <div className="voice-img reveal d1" data-anim-target><img src="https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=1200&q=85" alt="Sneha Iyer" /></div>
                <span className="role">The planner · Twelve weddings</span>
                <p className="quote">I had a <em>weekend</em> — for the first time in seven seasons.</p>
                <div className="who">Sneha Iyer — Cinnamon Events, Bangalore</div>
              </article>
              <article className="voice fade d3" data-anim-target>
                <div className="voice-img reveal d2" data-anim-target><img src="https://images.unsplash.com/photo-1519741497674-611481863552?w=1200&q=85" alt="Meera Shah" /></div>
                <span className="role">Mother of the bride</span>
                <p className="quote">It answered <em>kindly,</em> and faster than my son.</p>
                <div className="who">Meera Shah — Delhi · Mother of one</div>
              </article>
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section className="pricing" id="pricing" data-anim>
          <header className="pricing-head">
            <h2>
              <span className="line"><span>Simple pricing,</span></span>
              <span className="line d1"><span><em>no surprises.</em></span></span>
            </h2>
            <p className="fade d2" data-anim-target>Choose the plan that fits your wedding. Upgrade anytime.</p>
          </header>
          <div className="pricing-grid">
            {/* Starter */}
            <div className="price-card fade d1" data-anim-target>
              <div className="tier">Starter</div>
              <div className="price"><sup>$</sup>50</div>
              <div className="period">one-time payment</div>
              <div className="limits">
                <div className="limit"><div className="n">1</div><div className="l">Event</div></div>
                <div className="limit"><div className="n">200</div><div className="l">Guests</div></div>
              </div>
              <ul>
                <li>WhatsApp AI assistant</li>
                <li>RSVP tracking & reminders</li>
                <li>Guest FAQ automation</li>
                <li>Scheduled messages</li>
              </ul>
              <button onClick={handleGetStarted} className="btn-price">Get started</button>
            </div>

            {/* Pro - Featured */}
            <div className="price-card featured fade d2" data-anim-target>
              <div className="tier">Professional</div>
              <div className="price"><sup>$</sup>150</div>
              <div className="period">one-time payment</div>
              <div className="limits">
                <div className="limit"><div className="n">4</div><div className="l">Events</div></div>
                <div className="limit"><div className="n">800</div><div className="l">Guests</div></div>
              </div>
              <ul>
                <li>Everything in Starter</li>
                <li>Multi-event dashboard</li>
                <li>Vendor coordination</li>
                <li>Priority support</li>
              </ul>
              <button onClick={handleGetStarted} className="btn-price">Get started</button>
            </div>

            {/* Business */}
            <div className="price-card fade d3" data-anim-target>
              <div className="tier">Business</div>
              <div className="price"><sup>$</sup>300</div>
              <div className="period">one-time payment</div>
              <div className="limits">
                <div className="limit"><div className="n">10</div><div className="l">Events</div></div>
                <div className="limit"><div className="n">2000</div><div className="l">Guests</div></div>
              </div>
              <ul>
                <li>Everything in Professional</li>
                <li>Team collaboration</li>
                <li>Custom branding</li>
                <li>Dedicated support</li>
              </ul>
              <button onClick={handleGetStarted} className="btn-price">Get started</button>
            </div>
          </div>
        </section>

        {/* INVITE / CLOSER */}
        <section className="invite" id="invite" data-anim>
          <img className="kenburns" data-anim-target src="/wedding-photography/ACA01046-scaled.jpeg" alt="Couple at wedding ceremony" />
          <div className="cta-content">
            <div className="cta-top">
              <span className="eyebrow lt fade" data-anim-target>Get started</span>
              <h2>
                <span className="line"><span>Book a</span></span>
                <span className="line d1"><span><em>demo.</em></span></span>
              </h2>
              <p className="fade d2" data-anim-target style={{fontFamily:'var(--display)', fontStyle:'italic', fontSize:'18px', color:'rgba(246,241,230,.7)', maxWidth:'480px', margin:'18px auto 0'}}>See how Mandap handles RSVPs, messaging, and FAQ — with your real guest list.</p>
              <div className="cta-actions fade d3" data-anim-target>
                <a href="mailto:hello@mandap.ai?subject=Demo%20Request&body=Hi%2C%20I'd%20like%20to%20schedule%20a%20demo%20of%20Mandap." className="btn btn-primary" style={{background:'var(--gold)'}}>Request a demo</a>
                <button onClick={handleGetStarted} className="btn btn-ghost">Try it yourself</button>
              </div>
            </div>
            <div className="cta-foot fade d4" data-anim-target>
              <div className="col"><span className="l">Starts at</span><span className="v">$50</span></div>
              <div className="col"><span className="l">Set up in</span><span className="v">An afternoon</span></div>
              <div className="col"><span className="l">First message</span><span className="v">Tomorrow morning</span></div>
              <div className="col"><span className="l">Then —</span><span className="v">Read a <em>book.</em></span></div>
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="colophon">
          <div>
            <div className="brand-mark">Mandap<em>.</em></div>
            <p className="mission">A quiet concierge for the noisiest event of your life. Built by people who have, between them, planned more weddings than they care to admit.</p>
          </div>
          <div>
            <h4>The Product</h4>
            <ul>
              <li><a onClick={() => scrollToSection('feature-rsvp')}>RSVP concierge</a></li>
              <li><a onClick={() => scrollToSection('product')}>FAQ inbox</a></li>
              <li><a onClick={() => scrollToSection('feature-messages')}>Scheduled messages</a></li>
              <li><a onClick={() => scrollToSection('voices')} style={{cursor:'pointer'}}>Voices</a></li>
            </ul>
          </div>
          <div>
            <h4>For hosts</h4>
            <ul>
              <li><a onClick={handleGetStarted} style={{cursor:'pointer'}}>Begin an event</a></li>
              <li><a onClick={() => scrollToSection('pricing')} style={{cursor:'pointer'}}>Pricing</a></li>
              <li><a href="mailto:hello@mandap.ai">Planner programme</a></li>
              <li><a href="mailto:hello@mandap.ai">Onboarding call</a></li>
            </ul>
          </div>
          <div>
            <h4>Correspondence</h4>
            <ul>
              <li><a href="mailto:hello@mandap.ai">hello@mandap.ai</a></li>
              <li><a href="https://instagram.com/mandapai" target="_blank" rel="noopener noreferrer">Instagram</a></li>
              <li><a href="https://linkedin.com/company/mandapai" target="_blank" rel="noopener noreferrer">LinkedIn</a></li>
            </ul>
          </div>
          <div className="small">
            <span>© Mandap & Co. · All rights reserved</span>
            <span>Set in Fraunces & Inter</span>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingPage;
